<!-- 数据导入弹窗 -->
<template>
  <ele-modal
    form
    v-bind="attrs"
    :model-value="modelValue"
    :title="modelTitle"
    :width="width"
    :close-on-click-modal="false"
    @update:modelValue="updateModelValue"
    @opened="handleOpen"
    position="center"
    style="max-width: 95%"
  >
    <el-form
      ref="formRef"
      class="table-edit-form"
      @submit.prevent=""
      :label-width="labelWidth"
      :model="form"
      :rules="rules"
    >
      <el-row :gutter="12">
        <el-col
          :lg="24"
          :md="24"
          :sm="24"
          :xs="24"
          v-for="(column, index) in columns.filter((v) => v.isEdit && v.prop)"
          :key="index"
        >
          <template v-if="!column.isSelect">
            <el-form-item
              :key="index"
              :prop="column.prop"
              :label="LzdUtil.i18nFormItemLabel(columns, column.prop!)"
            >
              <el-input
                v-if="!column.isSwitch"
                clearable
                v-model.trim="form[column.prop!]"
                :type="
                  column.isNumber
                    ? 'number'
                    : column.isRows
                      ? 'textarea'
                      : 'text'
                "
                :readonly="isReadonly(column)"
                :placeholder="LzdUtil.i18nFormItemLabel(columns, column.prop!)"
              />
              <el-switch
                v-else
                v-model="form[column.prop!]"
                :active-value="1"
                :inactive-value="0"
              />
            </el-form-item>
          </template>
          <template v-if="column.items">
            <el-form-item
              :key="index"
              :prop="column.prop"
              :label="LzdUtil.i18nFormItemLabel(columns, column.prop!)"
            >
              <el-select
                clearable
                v-model="form[column.prop!]"
                :readonly="isReadonly(column)"
                :placeholder="
                  LzdUtil.i18nFormItemPlaceholder(columns, column.prop!)
                "
              >
                <el-option
                  v-for="(item, itemIndex) in Array.isArray(column.items)
                    ? column.items
                    : column.items()"
                  :label="item.l"
                  :value="item.v"
                  :key="itemIndex"
                />
              </el-select>
            </el-form-item>
          </template>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button @click="updateModelValue(false)">
        {{ t('global.btns.close') }}
      </el-button>
      <el-button type="primary" :loading="loading" @click="done">
        {{ t('global.btns.submit') }}
      </el-button>
    </template>
  </ele-modal>
</template>

<script lang="ts" setup>
  import { ref, watch, computed, useAttrs, nextTick } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useFormData } from '@/utils/use-form-data';

  import type { RP, LzdColumns, LzdColumn } from '@/api';
  import { RParam } from '@/api';
  import LzdUtil from '@/utils/lzd-util';
  import LzdAjax from '@/utils/lzd-ajax';
  import type { FormInstance, FormRules } from 'element-plus';

  const { t } = useI18n();
  const attrs = useAttrs();

  const emit = defineEmits<{
    (e: 'update:modelValue', modelValue: boolean): void;
    (e: 'done', value?: Record<string, any>): void;
  }>();

  /** 更新modelValue */
  const updateModelValue = (value: boolean) => {
    emit('update:modelValue', value);
    if (!value) {
      setTimeout(() => {
        formRef.value?.clearValidate();
        resetFields();
      }, 150);
    }
  };

  /** 参数定义 */
  const props = withDefaults(
    defineProps<{
      /** 弹窗是否打开 */
      modelValue: boolean;
      /** 弹窗标题 */
      title?: string;
      /** 弹窗宽度 */
      width?: number;
      /** 数据表格字段 */
      columns: LzdColumns;
      /** 数据 */
      data?: Record<string, any>;
      /** 宽度 */
      labelWidth?: string;
      /** 更新地址 */
      url?: string;
      /** 添加地址 */
      insertUrl?: string;
      /** 其他参数 */
      otherWhere?: Record<string, any>;
    }>(),
    {
      labelWidth: '150px',
      width: 740
    }
  );

  /** 提交状态 */
  const loading = ref(false);
  /** 只读字段 */
  const readonlyFields = ref<string[]>([]);

  /** 弹窗标题 */
  const modelTitle = computed(() => {
    if (props.title) {
      return props.title;
    }
    return !props.data?.seq ? t('global.btns.add') : t('global.btns.edit');
  });

  /** 是否只读 */
  const isReadonly = (column: LzdColumn) => {
    if (!props.data?.seq) {
      return false;
    }
    if (column.isReadonly) {
      readonlyFields.value.push(column.prop!);
      return true;
    }
    return false;
  };
  /** 获取请求地址 */
  const getApiUrl = () => {
    if (!props.url || !props.insertUrl) {
      return '';
    }
    if (!props.data?.seq) {
      return props.insertUrl ?? props.url ?? '';
    }
    return props.url ?? props.insertUrl ?? '';
  };

  /** 表单实例 */
  const formRef = ref<FormInstance | null>(null);

  /** 构造表单数据 */
  const buildUseFormData = () => {
    const data: Record<string, any> = {};
    // 数据填充
    props.columns
      .filter((v) => v.isEdit && v.prop)
      .forEach((v) => (data[v.prop!] = void 0));

    const fullData = props.data ?? {};
    Object.keys(data).forEach((k) => (data[k] = fullData[k]));

    // 其他值填充
    data.seq = fullData.seq;
    data.tenantId = fullData.tenantId;
    data.dataScopeId = fullData.dataScopeId;
    data.appId = fullData.appId;
    return useFormData<Record<string, any>>(data);
  };

  /** 表单数据 */
  const [form, resetFields, assignFields] = buildUseFormData();

  /** 表单验证规则 */
  const rules = computed<FormRules>(() => {
    const data: Record<string, any> = {};
    props.columns
      .filter((v) => v.isEdit && v.prop && v.isRequired)
      .forEach(
        (v) =>
          (data[v.prop!] = [
            {
              required: true,
              message: t('global.tips.required'),
              trigger: 'blur'
            }
          ])
      );
    return data;
  });

  /** 跳过的字段 */
  const skipFields = [
    'version',
    'seq',
    'tenantId',
    'tenantName',
    'dataScopeId',
    'appId'
  ];

  /** 修改 */
  async function update(data: Record<string, any>) {
    if (!data || !data.seq) {
      LzdUtil.tipsError({
        code: 1,
        message: '参数错误',
        messageEn: '參數錯誤',
        messageTw: 'Parameter Error'
      });
      return;
    }
    const item = { ...data };
    const pks = [item.seq];
    Object.keys(item).forEach((k) => {
      if (
        k.startsWith('created') ||
        k.startsWith('updated') ||
        k.startsWith('deleted') ||
        skipFields.includes(k) ||
        readonlyFields.value.includes(k)
      ) {
        delete item[k];
      }
    });

    return updateBy({
      update: RParam.buildEdit(item),
      pks: pks
    });
  }

  /** 修改 RP 格式 */
  async function updateBy(data: RP) {
    if (!data || !data.update || data.update.length === 0) {
      LzdUtil.tipsError({
        code: 1,
        message: '参数错误',
        messageEn: '參數錯誤',
        messageTw: 'Parameter Error'
      });
      return;
    }
    return LzdAjax.post<unknown>(getApiUrl(), data, false, true);
  }

  const done = () => {
    formRef.value?.validate?.((valid) => {
      if (!valid) {
        return;
      }
      const data = { ...(props.otherWhere ?? {}), ...(form ?? {}) };
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        updateModelValue(false);
        emit('done', (props.url ?? props.insertUrl) ? void 0 : data);
        return;
      }

      // 修改
      if (props.data?.seq) {
        update(data).then((_) => {
          updateModelValue(false);
          emit('done', (props.url ?? props.insertUrl) ? void 0 : data);
        });
      }
      // 添加
      else {
        LzdAjax.post<unknown>(apiUrl, data, false, true).then((_) => {
          updateModelValue(false);
          emit('done', (props.url ?? props.insertUrl) ? void 0 : data);
        });
      }
    });
  };

  // 打开弹窗
  const handleOpen = () => {
    readonlyFields.value = [];
    if (props.data) {
      assignFields(props.data);
    } else {
      resetFields();
    }
    nextTick(() => {
      nextTick(() => {
        formRef.value?.clearValidate?.();
      });
    });
  };
</script>

<script lang="ts">
  export default {
    name: 'LzdDataTableEdit'
  };
</script>
